import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import useTesterFeedback from "../../hooks/useTesterFeedback";
import useTesterProgramMode from "../../hooks/useTesterProgramMode";
import { dashboardPageMeta } from "../../config/dashboardPageMeta";
import ScreenshotUploader from "./ScreenshotUploader";
import TesterFeedbackHistory from "./TesterFeedbackHistory";
import PageTagOverlay from "./PageTagOverlay";
import toast from "react-hot-toast";
import {
  MessageSquare,
  X,
  Bug,
  Star,
  Gauge,
  Palette,
  Zap,
  ThumbsUp,
  Send,
  History,
  CheckCircle,
  Crosshair,
  PenLine,
} from "lucide-react";

const FEEDBACK_TYPES = [
  { id: "bug", label: "Bug Report", icon: Bug, color: "text-red-400" },
  { id: "ease-of-use", label: "Ease of Use", icon: Star, color: "text-blue-400" },
  { id: "feature-completeness", label: "Features", icon: Gauge, color: "text-purple-400" },
  { id: "design", label: "Design", icon: Palette, color: "text-pink-400" },
  { id: "performance", label: "Performance", icon: Zap, color: "text-yellow-400" },
  { id: "overall", label: "Overall", icon: ThumbsUp, color: "text-emerald-400" },
];

const SEVERITIES = [
  { id: "blocker", label: "Blocker", color: "bg-red-500/20 text-red-400 border-red-500/30" },
  { id: "major", label: "Major", color: "bg-orange-500/20 text-orange-400 border-orange-500/30" },
  { id: "minor", label: "Minor", color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" },
  { id: "cosmetic", label: "Cosmetic", color: "bg-slate-700 text-slate-300 border-slate-600" },
];

/**
 * `courseReview`: the owner's course walkthrough (Nov 3 2026). The tab sticks out
 * on the right edge of every page for her test learner and admin accounts, and
 * feedback is saved like tester feedback (with screenshots) so it can be worked through.
 */
export default function TesterFeedbackWidget({ isOpen: controlledOpen, onToggle, allowNonTester = false, courseReview = false }) {
  const { client } = useAuth();
  const location = useLocation();
  const { submitFeedback, submitting } = useTesterFeedback();
  const { testerProgramActive } = useTesterProgramMode();
  const isTester = courseReview || (testerProgramActive && client?.testerMode === true);

  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setIsOpen = onToggle ? (val) => onToggle(typeof val === "function" ? val(isOpen) : val) : setInternalOpen;
  const [activeTab, setActiveTab] = useState("submit"); // submit | history
  const [feedbackType, setFeedbackType] = useState("overall");
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState(0);
  const [npsScore, setNpsScore] = useState(null);
  const [severity, setSeverity] = useState(null);
  const [stepsToReproduce, setStepsToReproduce] = useState("");
  const [screenshotFiles, setScreenshotFiles] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [tagMode, setTagMode] = useState(false);
  const [pageTags, setPageTags] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  // Re-show feedback button when navigating to a new page
  useEffect(() => {
    setDismissed(false);
  }, [location.pathname]);

  // Auto-detect page
  const pageInfo = useMemo(() => {
    const path = location.pathname;
    const meta = dashboardPageMeta[path];
    return {
      page: path,
      pageTitle: meta?.title || path.split("/").filter(Boolean).pop() || "Unknown Page",
    };
  }, [location.pathname]);

  const handleScreenshotsChange = useCallback((files) => {
    setScreenshotFiles(files);
  }, []);

  const resetForm = () => {
    setFeedbackType("overall");
    setMessage("");
    setRating(0);
    setNpsScore(null);
    setSeverity(null);
    setStepsToReproduce("");
    setScreenshotFiles([]);
    setPageTags([]);
  };

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast.error("Please enter your feedback");
      return;
    }
    if (feedbackType === "bug" && screenshotFiles.length === 0) {
      toast.error("Please attach at least one screenshot for bug reports");
      return;
    }

    try {
      await submitFeedback({
        feedbackType,
        message: message.trim(),
        rating: feedbackType !== "bug" && feedbackType !== "overall" ? rating : null,
        npsScore: feedbackType === "overall" ? npsScore : null,
        severity: feedbackType === "bug" ? severity : null,
        stepsToReproduce: feedbackType === "bug" ? stepsToReproduce : null,
        screenshotFiles,
        pageTags: pageTags.length > 0 ? pageTags : null,
        courseReview,
        ...pageInfo,
      });

      toast.success("Feedback sent. Thank you!");
      resetForm();
      setSubmitted(true);

      if (isTester) {
        setTimeout(() => {
          setSubmitted(false);
          setActiveTab("history");
        }, 2000);
      } else {
        setTimeout(() => {
          setSubmitted(false);
          setIsOpen(false);
        }, 2000);
      }
    } catch (err) {
      // Upload security checks explain what was wrong with a file; anything else is generic
      const msg = err?.message || "";
      toast.error(/image|MB|screenshot|file/i.test(msg) ? msg : "Failed to submit feedback. Please try again.");
    }
  };

  // Only approved testers (or allowNonTester from chat) see the feedback widget
  if (!isTester && !allowNonTester) return null;

  // Full tester feedback widget
  if (dismissed && !isOpen) return null;
  return (
    <>
      {/* Course review: a tab that sticks out on the right edge, clear of the chat bubble */}
      {courseReview && (
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="hidden md:flex fixed right-0 top-1/2 -translate-y-1/2 z-50 flex-col items-center gap-1.5 px-2 py-4 rounded-l-xl bg-cyan-500 text-white font-bold text-xs shadow-lg shadow-cyan-500/30 hover:bg-cyan-400 hover:pr-3 transition-all"
          title="Course feedback (screenshot welcome)"
        >
          {isOpen ? <X size={16} /> : <PenLine size={16} />}
          <span style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>Feedback</span>
        </button>
      )}
      {/* Phones: a small labelled pill stacked just above the chat button, so both live in one corner */}
      {courseReview && (
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="md:hidden fixed right-4 bottom-20 z-50 h-9 px-3 rounded-full flex items-center gap-1.5 bg-cyan-500 text-white text-xs font-bold shadow-lg shadow-cyan-500/30 hover:bg-cyan-400 transition-all"
          aria-label="Course feedback"
          title="Course feedback"
        >
          {isOpen ? <X size={14} /> : <PenLine size={14} />}
          Feedback
        </button>
      )}

      {/* Floating Button — hidden when controlled from parent (e.g. chat icon) */}
      {!allowNonTester && !courseReview && (
        <div className="fixed bottom-28 right-6 z-50 flex items-center gap-1.5">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="px-5 py-2.5 rounded-full bg-cyan-500 text-white font-bold text-sm shadow-lg shadow-cyan-500/30 hover:bg-cyan-400 transition-all hover:scale-105 flex items-center gap-2"
            title="Send Feedback"
          >
            {isOpen ? <X size={16} /> : <MessageSquare size={16} />}
            Feedback
          </button>
          {!isOpen && (
            <button
              onClick={() => { setDismissed(true); setIsOpen(false); }}
              className="p-1.5 rounded-full bg-gray-700/80 text-gray-300 hover:bg-gray-600 hover:text-white transition-all"
              title="Dismiss"
            >
              <X size={14} />
            </button>
          )}
        </div>
      )}

      {/* Page Tag Overlay */}
      <PageTagOverlay
        active={tagMode}
        onTagsChange={setPageTags}
        onDeactivate={() => { setTagMode(false); setIsOpen(true); }}
      />

      {/* Panel */}
      {isOpen && (
        <div className={`fixed ${courseReview ? "left-4 right-4 bottom-32 md:left-auto md:bottom-auto md:top-1/2 md:-translate-y-1/2 md:right-14 md:w-[360px] md:max-w-[calc(100vw-4.5rem)]" : "bottom-[7.5rem] right-6 w-[360px] max-w-[calc(100vw-4.5rem)]"} z-50 max-h-[80vh] rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl overflow-hidden flex flex-col`}>
          {/* Header */}
          <div className="px-4 py-3 border-b border-slate-700 bg-cyan-500/10">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-cyan-400 flex items-center gap-2">
                <MessageSquare size={14} />
                {courseReview ? "Course Feedback" : "Customer Feedback"}
              </h3>
              <div className="flex items-center gap-2">
                <span className="text-[9px] px-2 py-0.5 rounded-full bg-slate-800 text-slate-400">
                  {pageInfo.pageTitle}
                </span>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
                  title="Close"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-slate-700">
            <button
              onClick={() => setActiveTab("submit")}
              className={`flex-1 py-2.5 text-xs font-bold transition-colors ${
                activeTab === "submit"
                  ? "text-cyan-400 border-b-2 border-cyan-400"
                  : "text-slate-500 hover:text-slate-300"
              }`}
            >
              <Send size={12} className="inline mr-1" />
              Submit
            </button>
            <button
              onClick={() => setActiveTab("history")}
              className={`flex-1 py-2.5 text-xs font-bold transition-colors ${
                activeTab === "history"
                  ? "text-cyan-400 border-b-2 border-cyan-400"
                  : "text-slate-500 hover:text-slate-300"
              }`}
            >
              <History size={12} className="inline mr-1" />
              My Past Feedback
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {activeTab === "history" ? (
              <TesterFeedbackHistory />
            ) : (
              <div className="p-4 space-y-4">
                {/* Feedback Type Selector */}
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                    Category
                  </label>
                  <div className="grid grid-cols-3 gap-1.5">
                    {FEEDBACK_TYPES.map((type) => {
                      const Icon = type.icon;
                      return (
                        <button
                          key={type.id}
                          onClick={() => { setFeedbackType(type.id); setRating(0); setNpsScore(null); setSeverity(null); }}
                          className={`flex flex-col items-center gap-1 p-2 rounded-lg text-[10px] font-bold transition-all border ${
                            feedbackType === type.id
                              ? "bg-cyan-500/10 border-cyan-500/40 text-cyan-400"
                              : "border-slate-700 text-slate-400 hover:border-slate-600"
                          }`}
                        >
                          <Icon size={14} className={feedbackType === type.id ? "text-cyan-400" : type.color} />
                          {type.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Bug-specific: Severity */}
                {feedbackType === "bug" && (
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                      Severity
                    </label>
                    <div className="flex gap-1.5">
                      {SEVERITIES.map((s) => (
                        <button
                          key={s.id}
                          onClick={() => setSeverity(s.id)}
                          className={`px-3 py-1.5 rounded-lg text-[10px] font-bold border transition-colors ${
                            severity === s.id ? s.color : "border-slate-700 text-slate-500"
                          }`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Rating (1-5) for non-bug, non-overall */}
                {feedbackType !== "bug" && feedbackType !== "overall" && (
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                      Rating
                    </label>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={n}
                          onClick={() => setRating(n)}
                          className="p-1 transition-transform hover:scale-110"
                        >
                          <Star
                            size={20}
                            className={n <= rating ? "text-yellow-400 fill-yellow-400" : "text-slate-600"}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* NPS (0-10) for overall */}
                {feedbackType === "overall" && (
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                      How likely are you to recommend TheScope180? (0-10)
                    </label>
                    <div className="flex gap-1">
                      {Array.from({ length: 11 }).map((_, n) => (
                        <button
                          key={n}
                          onClick={() => setNpsScore(n)}
                          className={`w-7 h-7 rounded text-[10px] font-bold transition-colors border ${
                            npsScore === n
                              ? n >= 9
                                ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-400"
                                : n >= 7
                                ? "bg-yellow-500/20 border-yellow-500/40 text-yellow-400"
                                : "bg-red-500/20 border-red-500/40 text-red-400"
                              : "border-slate-700 text-slate-500 hover:border-slate-600"
                          }`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Message */}
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                    {feedbackType === "bug" ? "Describe the Bug" : "Your Feedback"}
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={
                      feedbackType === "bug"
                        ? "What went wrong? What did you expect to happen?"
                        : "Share your thoughts..."
                    }
                    className="w-full h-20 rounded-xl bg-slate-800 border border-slate-700 p-3 text-sm text-white placeholder-slate-500 resize-none outline-none focus:border-cyan-500/50 transition-colors"
                  />
                </div>

                {/* Bug-specific: Steps to Reproduce */}
                {feedbackType === "bug" && (
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                      Steps to Reproduce
                    </label>
                    <textarea
                      value={stepsToReproduce}
                      onChange={(e) => setStepsToReproduce(e.target.value)}
                      placeholder="1. Go to...&#10;2. Click on...&#10;3. See error"
                      className="w-full h-16 rounded-xl bg-slate-800 border border-slate-700 p-3 text-sm text-white placeholder-slate-500 resize-none outline-none focus:border-cyan-500/50 transition-colors"
                    />
                  </div>
                )}

                {/* Screenshots */}
                <ScreenshotUploader
                  onScreenshotsChange={handleScreenshotsChange}
                  required={feedbackType === "bug"}
                />

                {/* Tag an Element */}
                <button
                  onClick={() => { setTagMode(true); setIsOpen(false); }}
                  className="w-full py-2.5 rounded-xl border border-slate-600 text-slate-300 text-xs font-bold hover:border-cyan-500/50 hover:text-cyan-400 transition-all flex items-center justify-center gap-2"
                >
                  <Crosshair size={14} />
                  Tag an Element on This Page
                  {pageTags.length > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400 text-[10px]">
                      {pageTags.length}
                    </span>
                  )}
                </button>

                {/* Submit */}
                <button
                  onClick={handleSubmit}
                  disabled={!message.trim() || submitting || submitted}
                  className={`w-full py-3 rounded-xl font-bold text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                    submitted
                      ? "bg-emerald-500 text-white"
                      : "bg-gradient-to-r from-cyan-500 to-teal-500 text-slate-900 hover:from-cyan-400 hover:to-teal-400"
                  }`}
                >
                  {submitted ? (
                    <>
                      <CheckCircle size={14} />
                      Submitted
                    </>
                  ) : submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-slate-900/30 border-t-slate-900 rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send size={14} />
                      Submit Feedback
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
